import type { MessagesShape } from '../types';
import type { Messages } from './ar';

export const en: MessagesShape<Messages> = {
  app: {
    skipToContent: 'Skip to content',
    loading: 'Just a moment…',
    back: 'Back',
    close: 'Close',
    continue: 'Continue',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    next: 'Next',
    start: 'Start',
    delete: 'Delete',
    yes: 'Yes',
    no: 'No',
    sampleContent: 'Sample content',
    sampleContentNotice:
      'These activities are examples that a teacher has not validated yet, and they may differ from the class programme.',
    offlineReady: '{app} now works without the Internet.',
    updateAvailable: 'A new version of {app} is available.',
    updateNow: 'Update',
    later: 'Later',
    mainNavigation: 'Main navigation',
  },
  errors: {
    title: 'Something went wrong',
    body: 'This page could not be shown. Your progress is saved.',
    reload: 'Reload',
    goHome: 'Back to start',
    notFound: 'We could not find this page.',
    storageTitle: 'Progress cannot be saved in this browser',
    storageUnavailable:
      'The browser seems to block local storage (for example in private browsing). Open {app} in a normal window.',
    storageQuota: 'Storage is full. Free some space on the device and try again.',
    storageBlocked:
      '{app} is open in another tab with a different version. Close the other tabs and reload.',
    storageUnknown: 'Local storage could not be opened.',
    saveFailed:
      'Your last answer could not be saved. You can keep playing, but this session may not be saved.',
    contentMissing: 'No activities are available here yet.',
  },
  welcome: {
    title: 'Welcome to {app}',
    subtitle:
      'Short learning games in Arabic, maths, languages and science, from preschool to year 6.',
    start: 'Let’s go!',
    privacyTitle: 'Your child’s privacy first',
    privacy1: 'No email, no phone number, no real name.',
    privacy2: 'All progress stays on this device.',
    privacy3: 'No ads and no trackers.',
    parentArea: 'Parent area',
  },
  profiles: {
    title: 'Who is learning today?',
    add: 'Add a learner',
    play: 'Start with {name}',
    anonymous: 'Learner',
    level: 'Class: {level}',
    switch: 'Switch learner',
  },
  onboarding: {
    title: 'New learner',
    step: 'Step {current} of {total}',
    avatarTitle: 'Choose your character',
    nicknameLabel: 'Nickname (optional)',
    nicknameHint: 'For example “Star” or “Champion”. Don’t write your full name.',
    levelTitle: 'Which class are you in?',
    levelHint: 'A parent picks the child’s actual class, not their age.',
    schoolTypeTitle: 'Type of school',
    schoolTypeHint: 'For now we use the national programme for public and private schools.',
    variantTitle: 'Programme',
    languageTitle: 'App language',
    preferencesTitle: 'Preferences',
    soundEffects: 'Gentle sounds after answers',
    speech: '“Listen” button to read words aloud',
    largeText: 'Large text',
    reducedMotion: 'Less motion',
    finish: 'Create profile',
    nicknameTooLong: 'The nickname is too long (24 characters at most).',
    color: 'Colour {number}',
    colorGroup: 'Background colour',
  },
  avatars: {
    owl: 'Owl',
    cat: 'Cat',
    lion: 'Lion',
    turtle: 'Turtle',
    fish: 'Fish',
    bee: 'Bee',
    rabbit: 'Rabbit',
    fox: 'Fox',
  },
  nav: {
    home: 'Home',
    subjects: 'Subjects',
    rewards: 'Rewards',
    review: 'Review',
    settings: 'Settings',
    parent: 'Parent',
  },
  home: {
    greeting: 'Hello {name}!',
    greetingAnonymous: 'Hello!',
    subtitle: 'What shall we learn today?',
    continueTitle: 'Keep learning',
    continueCta: 'Let’s go',
    missionTitle: 'Today’s mission',
    missionBody: '{count} short questions on different topics.',
    missionDone: 'Today’s mission is done. Well done!',
    missionCta: 'Start the mission',
    missionAgain: 'Practise more',
    subjects: 'Subjects',
    garden: 'My garden',
    gardenHint: 'Every goal you master becomes a flower in your garden.',
    flowers: { zero: 'No flowers yet', one: '{count} flower', other: '{count} flowers' },
    rewardsLink: 'Rewards',
    reviewLink: 'Review',
    reviewDue: {
      zero: 'No review today',
      one: '{count} review is waiting',
      other: '{count} reviews are waiting',
    },
  },
  subjects: {
    title: 'Subjects',
    objectives: { zero: 'No goals', one: '{count} goal', other: '{count} goals' },
    none: 'No subjects are available for this class yet.',
  },
  map: {
    title: 'Learning map',
    earlier: 'Review earlier years',
    open: 'Open',
    empty: 'There are no goals for this subject in your class yet.',
  },
  mastery: {
    status: {
      notStarted: 'Not started',
      learning: 'Learning',
      needsPractice: 'Needs practice',
      progressing: 'Progressing',
      provisionalMastery: 'Provisional mastery',
    },
    reason: {
      notStarted: 'No answers recorded yet.',
      gatheringEvidence:
        'Only a few answers so far ({answered}): more practice is needed before any estimate.',
      consistentAcrossDays:
        '{percent}% of recent answers were right first time, across {days} different days.',
      needsConfirmation:
        '{percent}% of recent answers were right. Practice on another day will confirm mastery.',
      mostlyCorrect: '{percent}% of recent answers were right first time.',
      frequentMistakes: 'Only {percent}% of recent answers were right first time.',
      mixedResults: 'Recent results are mixed ({percent}% right first time).',
    },
    trend: {
      improving: 'Improving',
      stable: 'Stable',
      declining: 'Lower recently',
      unknown: 'Not known yet',
    },
    estimateNote:
      'An estimate based on recorded practice, not a judgement of the child’s abilities.',
  },
  lesson: {
    practise: 'Let’s practise',
    chooseGame: 'Choose a game',
    recommended: 'Suggested',
    noGames: 'No games are available for this goal yet.',
    workedExample: 'Worked example',
    tip: 'Tip',
    rule: 'Rule',
    examples: 'Examples',
    status: 'Your progress',
  },
  rules: {
    title: 'My rule book',
    link: {
      zero: 'My rule book',
      one: 'My rule book: {count} rule',
      other: 'My rule book: {count} rules',
    },
    empty: 'No rules in this subject yet.',
    openLesson: 'Open the lesson: {lesson}',
  },
  games: {
    'quick-quiz': { name: 'Quick quiz', description: 'Short, varied questions.' },
    'find-correct': {
      name: 'Find the right answer',
      description: 'Pick the right answer from a few choices.',
    },
    'missing-number': {
      name: 'Missing number',
      description: 'Find the hidden number in the calculation.',
    },
    'word-builder': { name: 'Word builder', description: 'Put the letters in order.' },
    'letter-sound': {
      name: 'Letters and sounds',
      description: 'Match letters with their sounds and words.',
    },
    'sentence-builder': { name: 'Sentence builder', description: 'Put the words in order.' },
    memory: { name: 'Memory cards', description: 'Turn over cards and find the pairs.' },
    categorize: { name: 'Sorting game', description: 'Put each item in its group.' },
    'true-false': { name: 'True or false', description: 'Decide whether the sentence is right.' },
    'daily-mission': {
      name: 'Today’s mission',
      description: 'A short mix of review and practice.',
    },
  },
  voice: {
    answer: 'Answer with your voice',
    stop: 'Stop',
    listening: 'I’m listening… say your answer.',
    heard: 'I heard: “{text}”.',
    notUnderstood: 'I didn’t understand the answer. Try again or tap your answer.',
    error: {
      'not-allowed': 'The microphone is not allowed. Ask a grown-up to allow it in the browser.',
      'no-speech': 'I didn’t hear anything. Speak clearly near the device.',
      unavailable: 'Voice answers are not available in this browser.',
      aborted: 'Listening stopped.',
      failed: 'Voice recognition is not working right now. You can answer by tapping.',
    },
    setting: 'Voice answers (test)',
    settingDescription:
      'When on, your child can answer some questions by speaking. The browser turns the voice into text: in Chrome and on Android the voice is sent to Google for this; in Safari, to Apple or processed on the device. {app} never records, keeps or sends the voice to its server.',
    unsupported: 'This browser cannot recognise speech. Try Chrome or Safari.',
  },
  vote: {
    title: 'Vote for the next version',
    nav: 'Vote',
    intro:
      'Choose up to 3 ideas for {app}. The most wanted are built first and announced in “What’s new?”. You can choose together with your children.',
    choose: 'Choose up to {max} ideas',
    chosen: '{count} of {max}',
    ideaLabel: 'Another idea? Write it here (optional)',
    ideaHint: 'Please don’t write names or personal details about children.',
    send: 'Send',
    thanks: 'Your vote has arrived. Thank you for helping us choose what comes next!',
    alreadyVoted: 'You have already voted in this round. Thank you!',
    alreadyVotedHint: 'A new round opens with the next version. You can always send a new idea.',
    ideas: {
      'journey-map': {
        title: 'Journey map',
        description: 'The ObiGo 4x4 drives through Tunisian places as goals are mastered.',
      },
      'recorded-audio': {
        title: 'Recorded voices',
        description: 'A Tunisian teacher says the letters, sounds and words.',
      },
      'teacher-review': {
        title: 'Checked by teachers',
        description: 'Teachers review every question and lesson.',
      },
      'home-tips': {
        title: 'Tips for home',
        description: 'A small activity at home for each difficulty in the report.',
      },
      printables: {
        title: 'Printable sheets',
        description: 'The weekly report and exercises as PDF.',
      },
      stories: {
        title: 'Picture stories',
        description: 'Short stories set in Tunisia, with comprehension games.',
      },
      'play-together': {
        title: 'Play together',
        description: 'A parent or sibling asks, the child answers on the same device.',
      },
      'reading-comfort': {
        title: 'Comfortable reading',
        description: 'Dyslexia-friendly options and clearer colours.',
      },
      'packs-on-demand': {
        title: 'Download by year',
        description: 'Download only your child’s year to keep the app small.',
      },
      'content-stats': {
        title: 'Anonymous statistics (with consent)',
        description: 'To find hard or wrong questions quickly.',
      },
      'lighter-app': {
        title: 'A faster app',
        description: 'Faster on basic phones and slow connections.',
      },
    },
  },
  whatsNew: {
    title: 'What’s new?',
    intro: 'What changed in each version, often thanks to your feedback.',
    version: '{app} — version {version}',
    latest: 'Latest',
    banner: 'New in {app} (version {version})',
    seeAll: 'See everything new',
    dismiss: 'OK',
    voteHint: 'What would you like in the next version?',
    voteLink: 'Vote and share your ideas (parent area)',
  },
  play: {
    progress: 'Question {current} of {total}',
    check: 'Check',
    hint: 'Hint',
    hintTitle: 'Hint',
    correct1: 'Well done!',
    correct2: 'Great, that’s right!',
    correct3: 'Excellent!',
    correctAfterRetry: 'Well done! Your second try was right.',
    tryAgain: 'Not quite yet. Have another go!',
    tryAgainButton: 'Try again',
    revealed: 'That’s okay, we learn together! The right answer is:',
    revealedNoAnswer: 'That’s okay, we learn together! Look at the solution:',
    explanation: 'How do we think about it?',
    showExplanation: 'Explain it to me',
    hideExplanation: 'Hide explanation',
    next: 'Next question',
    finish: 'Finish',
    quit: 'Leave',
    quitTitle: 'Do you want to leave?',
    quitBody: 'The answers you gave will be saved.',
    quitConfirm: 'Yes, leave',
    stay: 'Keep playing',
    listen: 'Listen',
    stop: 'Stop',
    true: 'True',
    false: 'False',
    yourAnswer: 'Your answer',
    clear: 'Clear',
    backspace: 'Delete last digit',
    answerArea: 'Answer area',
    tilesArea: 'Available letters and words',
    tapToPlace: 'Tap the cards in order. Tap a card in your answer to send it back.',
    emptyAnswer: 'Answer first, then press “Check”.',
    matchHelp: 'Pick an item on the left, then its partner on the right.',
    matchHelpLtr: 'Pick an item in the first list, then its partner in the second list.',
    matched: 'Matched with {item}',
    pairNumber: 'Pair {number}',
    memoryHelp: 'Turn over two cards at a time.',
    memoryCard: 'Card {number}',
    memoryFound: 'Found {found} of {total}',
    categorizeHelp: 'Pick an item, then tap the right group.',
    selected: 'selected',
    putIn: 'Put in “{category}”',
    unsorted: 'Items to sort',
    wrongParts: 'Some answers need another look: check the marks.',
    preview: 'The word',
    noExercises: 'No questions are available for this game yet.',
    saving: 'Saving…',
    numberLine: 'Number line from {min} to {max}',
    countAria: '{count} × {label}',
    blank: 'blank',
  },
  results: {
    title: 'Session complete!',
    stars: 'You earned {count} of 3 stars',
    summary: 'Right answers: {correct} of {total}.',
    firstTry: '{count} of them first time.',
    encourage3: 'Careful, excellent work!',
    encourage2: 'Lovely progress, keep going!',
    encourage1: 'Every try makes you stronger. We’ll practise again together.',
    newAchievements: 'New reward!',
    playAgain: 'Play again',
    backToMap: 'Back to the map',
    home: 'Home',
    nextStep: 'Next step',
  },
  rewards: {
    title: 'My rewards',
    earned: 'Earned',
    locked: 'Not earned yet',
    progress: '{current} of {target}',
    garden: 'My garden',
    gardenEmpty: 'Practise a goal on different days to grow your first flower.',
  },
  achievements: {
    'first-steps': { title: 'First step', description: 'Finish your first session.' },
    'math-explorer': { title: 'Number explorer', description: 'Finish 5 maths sessions.' },
    'letter-friend': { title: 'Friend of letters', description: 'Finish 5 Arabic sessions.' },
    'curious-scientist': { title: 'Curious scientist', description: 'Finish 5 science sessions.' },
    'globe-trotter': {
      title: 'Knowledge traveller',
      description: 'Practise in 3 different subjects.',
    },
    'never-give-up': {
      title: 'Never give up',
      description: 'Get it right on the second try 10 times.',
    },
    'good-helper': { title: 'Hint user', description: 'Use a hint and then answer right 5 times.' },
    'first-flower': { title: 'First flower', description: 'Master your first goal.' },
    gardener: { title: 'Gardener', description: 'Master 5 goals.' },
    'mission-1': { title: 'First mission', description: 'Finish your first daily mission.' },
    'mission-7': { title: 'Seven missions', description: 'Finish 7 daily missions (any time).' },
    reviewer: { title: 'Active reviewer', description: 'Finish 3 review sessions.' },
    'careful-work': { title: 'Careful work', description: 'Get 3 stars in a session.' },
  },
  review: {
    title: 'Suggested review',
    intro: 'These goals deserve a short practice now.',
    empty: 'Nothing to review right now. Come back later!',
    start: 'Start',
  },
  recommend: {
    kind: {
      review: 'Review',
      prerequisite: 'Important basics',
      easier: 'Gentle practice',
      practice: 'Keep going',
      next: 'New goal',
      start: 'Let’s begin',
    },
    reason: {
      review: {
        zero: 'Practised today. A short review helps remember.',
        one: 'Last practised {count} day ago. A short review helps remember.',
        other: 'Last practised {count} days ago. A short review helps remember.',
      },
      prerequisite: 'The goal “{objective}” needs more practice, and this goal is a basis for it.',
      easier: 'Recent results ({percent}%) suggest easier exercises with hints.',
      practice:
        '{percent}% of recent answers are right. A bit more practice will strengthen learning.',
      next: 'Earlier goals are going well, so this is the next one.',
      start: 'The first goal in this subject.',
    },
  },
  mistakes: {
    'off-by-one': 'answers off by one (counting)',
    'wrong-operation': 'using a different operation',
    'place-value': 'mixing up ones, tens and hundreds',
    regrouping: 'carrying and exchanging tens',
    'smaller-from-larger': 'subtracting the smaller digit from the larger one in each column',
    'table-slip': 'mixing up neighbouring times-table results',
    counting: 'counting objects',
    'similar-letter-shape': 'letters that look alike',
    'similar-sound': 'sounds that are close',
    'letter-position': 'letter shapes by position',
    harakat: 'short vowel marks',
    'letter-order': 'letter order',
    'word-order': 'word order',
    spelling: 'spelling',
    article: 'choosing the article',
    agreement: 'singular and plural agreement',
    conjugation: 'verb forms',
    vocabulary: 'word meanings',
    comprehension: 'understanding the text',
    'grammar-role': 'the role of words in a sentence',
    classification: 'sorting',
    misconception: 'a science idea to clarify',
    unknown: 'various mistakes',
  },
  parent: {
    gate: {
      title: 'Parent area',
      intro: 'Enter the parent PIN.',
      pin: 'Parent PIN',
      unlock: 'Enter',
      wrong: {
        zero: 'Wrong PIN.',
        one: 'Wrong PIN. {count} try left.',
        other: 'Wrong PIN. {count} tries left.',
      },
      locked: 'Too many tries. Try again in {seconds} seconds.',
      forgot: 'Forgot the PIN?',
      checking: 'Checking…',
      childNote: 'This area is for parents.',
    },
    setup: {
      title: 'Create a parent PIN',
      intro: 'This PIN keeps children from entering the parent area by accident.',
      pin: 'PIN of 4 to 6 digits',
      confirm: 'Type the PIN again',
      mismatch: 'The two PINs do not match.',
      format: 'The PIN has 4 to 6 digits.',
      tooSimple: 'Choose a PIN that is not repeated or consecutive digits.',
      create: 'Create PIN',
      limitation:
        'Note: the PIN keeps children out, but it does not protect the data from someone with full access to the device.',
    },
    recovery: {
      title: 'Recovery code',
      intro:
        'Keep this code somewhere safe (on paper, for example). It is used if you forget the PIN and will not be shown again.',
      copy: 'Copy',
      copied: 'Copied',
      saved: 'I have written the recovery code down somewhere safe',
      continue: 'Continue',
    },
    reset: {
      title: 'Recover access',
      code: 'Recovery code',
      newPin: 'New parent PIN',
      submit: 'Change PIN',
      wrongCode: 'The recovery code is not correct.',
    },
    dashboard: {
      title: 'Parent area',
      lock: 'Lock',
      learners: 'Learners',
      noLearners: 'There are no learner profiles yet.',
      open: 'View report',
      dataLink: 'Data and privacy',
      settingsLink: 'Security',
    },
    report: {
      title: '{name}’s report',
      anonymousChild: 'your child',
      level: 'Class: {level}',
      estimateNote:
        'All figures come only from exercises recorded on this device. They estimate progress; they are not a school assessment.',
      learningTime: 'Learning time',
      sessions: 'Sessions completed',
      exercises: 'Questions answered',
      accuracy: 'Right first time',
      thisWeek: 'This week',
      allTime: 'Since the start',
      bySubject: 'By subject',
      answered: 'Questions: {count}',
      mastered: 'Goals with provisional mastery',
      needsPractice: 'Goals that need practice',
      inProgress: 'Goals in progress',
      none: 'Nothing yet.',
      improvement: 'Progress over the weeks',
      weekOf: 'Week of {date}',
      notEnoughWeeks: 'At least two weeks of practice are needed to show progress over time.',
      recentSessions: 'Recent sessions',
      noSessions: 'No sessions recorded yet.',
      recommended: 'Suggested activities',
      weekly: 'This week’s summary',
      print: 'Print report',
      export: 'Export this learner’s data',
      accuracyTable: 'Accuracy by subject',
      chartAlt: 'Share of answers right first time, by week',
      abandoned: 'Unfinished session',
      why: 'Why?',
    },
    summary: {
      practised: '{name} practised: {objectives}.',
      strong: '“{objective}”: mostly right ({correct} of {answered} first time).',
      developing: '“{objective}”: improving ({correct} of {answered} first time).',
      needsPractice: '“{objective}” needs more practice ({correct} of {answered}).',
      needsPracticeMistake: '“{objective}” needs more practice, especially {mistake}.',
      fewAnswers: 'Early estimate: only a few answers so far ({count}).',
      mastery: 'Provisional mastery of “{objective}”: steady results over several days.',
      perseverance: 'Answers corrected on the second try: {count}. Lovely perseverance!',
      nextStep: 'Suggested next step: {step}',
      weekSessions:
        'This week — sessions: {sessions}, learning time: about {minutes}, questions: {answered}.',
      weekAccuracy: '{percent}% of answers right first time.',
      weekMastered: 'New goals with provisional mastery: {objectives}.',
      weekNeeds: 'Needs practice: {objectives}.',
      weekEmpty: 'No sessions this week.',
    },
    security: {
      title: 'Security',
      changePin: 'Change parent PIN',
      currentPin: 'Current PIN',
      newPin: 'New PIN',
      pinChanged: 'The PIN has been changed.',
      rotate: 'Create a new recovery code',
      rotateHint: 'The old recovery code will stop working.',
    },
  },
  settings: {
    title: 'Settings',
    language: 'App language',
    preferences: '{name}’s preferences',
    deviceLanguageHint: 'This language is used before a learner is chosen.',
    profile: 'Profile',
    level: 'Class',
    editProfile: 'Save changes',
    saved: 'Saved.',
    privacyLink: 'Privacy and data',
    contentStatus: 'Content status',
    contentVersion: 'Content version: {version}',
  },
  privacy: {
    title: 'Privacy and data',
    intro: '{app} works without an account and without collecting personal data.',
    stored:
      'What is stored: a learner profile with a random id, the class, an optional nickname, answers and progress — only on this device.',
    notStored:
      'We never ask for a real name, birth date, school name or contact details, and nothing is sent to a server.',
    noTrackers: 'No ads, analytics or tracking tools.',
    localWarningTitle: 'Important: data is local',
    localWarning:
      'Clearing browser data, uninstalling the app or losing the device deletes all progress. Export a backup regularly.',
    persist: 'Ask the browser for persistent storage',
    persistGranted: 'The browser granted persistent storage.',
    persistDenied: 'The browser declined. Export a backup regularly.',
    persistUnsupported: 'This browser cannot request persistent storage.',
    backupTitle: 'Backup',
    backupHowTo:
      'Press “Export” to download a file. Keep it somewhere private (it is not encrypted). To restore it on this or another device, use “Import”.',
    exportAll: 'Export all data',
    exported: 'The file {file} was created.',
    importTitle: 'Import a backup',
    importChoose: 'Choose a backup file (.json)',
    importPreview: {
      zero: 'The file contains no learners.',
      one: 'The file contains {count} learner.',
      other: 'The file contains {count} learners.',
    },
    importConflict: 'Some of these profiles already exist on this device.',
    importReplace: 'Replace existing',
    importCopy: 'Add as a copy',
    importConfirm: 'Import',
    importDone: 'Import complete.',
    importErrors: {
      tooLarge: 'The file is too large.',
      notJson: 'This file is not a valid backup.',
      wrongFormat: 'This file does not come from {app}.',
      newerVersion: 'This file was made by a newer version of {app}. Update the app first.',
      invalid: 'The file is damaged or incomplete; nothing was changed.',
      inconsistent: 'The file content is inconsistent; nothing was changed.',
      empty: 'The file is empty.',
      readFailed: 'The file could not be read.',
    },
    deleteTitle: 'Delete data',
    deleteLearner: 'Delete {name}’s profile',
    deleteLearnerBody:
      'The profile, its answers and rewards will be permanently deleted from this device.',
    deleteAll: 'Delete everything',
    deleteAllBody:
      'All profiles, progress and the parent PIN will be permanently deleted from this device.',
    typeToConfirm: 'Type “{word}” to confirm',
    confirmWord: 'DELETE',
    deleted: 'Deleted.',
  },
  feedback: {
    rateTitle: 'Did you like this game?',
    liked: 'I liked it',
    soso: 'It was OK',
    disliked: 'I didn’t like it',
    rateThanks: 'Thank you! Your parent will see what you think.',
    report: 'Report a problem',
    reportTitle: 'What is wrong with this question?',
    reasons: {
      unclear: 'The question is not clear',
      wrongAnswer: 'I think the answer is wrong',
      tooHard: 'It is too hard',
      something: 'Something else',
    },
    reportSend: 'Send it to my parent',
    reportThanks: 'Thank you! Your parent will see your note.',
  },
  parentFeedback: {
    title: 'Feedback and ideas',
    intro:
      'Your feedback helps us improve {app}. We read every message and fix mistakes in the next updates.',
    privacyNote:
      'We never send children’s ids, nicknames or results — only what you write here and the ready-made answers the children picked.',
    childrenTitle: 'What the children reported',
    childrenEmpty: 'No new notes from the children.',
    includeChildren: 'Include the children’s notes',
    ratingEntry: '“{game}”: {mood}',
    problemEntry: 'Question in “{objective}”: {reason}',
    formTitle: 'Your message',
    role: 'I am a',
    roles: { parent: 'parent', teacher: 'teacher', another: 'other' },
    topic: 'Topic',
    topics: {
      idea: 'Idea or suggestion',
      problem: 'Problem in the app',
      content: 'Mistake in learning content',
      praise: 'Something you liked',
      misc: 'Something else',
    },
    message: 'Message',
    messageHint: 'Please don’t write names or personal details about the child.',
    contact: 'Contact e-mail (optional)',
    contactHint: 'Leave empty if you don’t need an answer. It is used for nothing else.',
    technical: 'Include anonymous technical details (versions, language, screen size)',
    preview: 'Exactly what will be sent',
    send: 'Send',
    sending: 'Sending…',
    sent: 'Your message has arrived. Thank you!',
    empty: 'Write a message or include the children’s notes before sending.',
    errorOffline: 'No Internet connection. Try again later or copy the message.',
    errorRejected: 'The message could not be accepted (it may be too long).',
    errorServer: 'The service is unavailable right now. Try again later or copy the message.',
    copy: 'Copy the message',
    copied: 'Copied',
    email: 'Send by e-mail',
  },
  units: {
    minutes: { zero: 'less than a minute', one: '{count} minute', other: '{count} minutes' },
  },
};
